import cx from "classnames";
import { Fragment } from "react";

import { toDate } from "common/utils/dates";
import { isValueSet } from "common/utils";
import { toHumanizedList } from "common/utils/strings";

import { IProductLineDetails } from "client/products/types";
import { ControlledSubstancesSummary } from "client/carts/types";
import ControlledSubstanceStateNotice from "client/controlledSubstances/components/ControlledSubstanceStateNotice";
import Banner from "client/common/components/content/Banner";
import DoctorImage from "client/common/components/images/DoctorImage";

import styles from "../styles/ProductLinePrescriptions.module.less";

type ProductLinePrescriptionBannerProps = {
  image: string;
  label: React.ReactNode;
  description: React.ReactNode;
  children?: React.ReactNode;
};

const ProductLinePrescriptionBanner = ({
  image,
  label,
  description,
  children,
}: ProductLinePrescriptionBannerProps) => {
  return (
    <Banner className={styles.container}>
      <div className={styles.bannerHeader}>
        <DoctorImage className={styles.doctorImage} src={image} />
        <Banner.Content
          label={<span className={styles.bannerLabel}>{label}</span>}
          description={<span className={styles.bannerDescription}>{description}</span>}
        />
      </div>
      {children}
    </Banner>
  );
};

type ProductLinePrescriptionsProps = {
  productLine: IProductLineDetails;
  controlledSubsSummary: ControlledSubstancesSummary | undefined;
};

const ProductLinePrescriptions = ({
  productLine,
  controlledSubsSummary,
}: ProductLinePrescriptionsProps) => {
  const prescriptions = productLine.products.flatMap((product) =>
    product.items.flatMap((item) =>
      item.active_prescriptions.map((prescription) => ({
        ...prescription,
        facts: [
          {
            label: "Pet Name",
            value: prescription.patient.name_display,
            className: styles.pet,
          },
          {
            label: "Strength",
            value: prescription.flexscriptions_enabled ? "Any" : product.name,
          },
          {
            label: "Expires on",
            value: prescription.expiration_date && toDate(prescription.expiration_date),
            className: styles.expires,
          },
          {
            label: "Fills",
            value: prescription.remaining_fills_display,
          },
          {
            label: "Qty per fill",
            value: prescription.flexscriptions_enabled
              ? "Any"
              : prescription.quantity_per_fill_display,
          },
        ],
      })),
    ),
  );

  if (!productLine.prescription_required || !productLine.doctor) {
    return null;
  }

  if (!prescriptions.length) {
    return (
      <ProductLinePrescriptionBanner
        image={productLine.doctor.image_url}
        label="Veterinarian approval required"
        description={
          <span>
            {productLine.doctor.name_display} will review your medication request.{" "}
            <span className={styles.long}>
              If you aren&apos;t sure of correct size and dose, please contact them directly.
            </span>
          </span>
        }
      />
    );
  }

  return (
    <ProductLinePrescriptionBanner
      image={productLine.doctor.image_url}
      label={
        <span>
          Prescribed for{" "}
          {toHumanizedList(
            new Set(prescriptions.map((prescription) => prescription.patient.name_display)),
          )}
        </span>
      }
      description={
        <>
          <span className={styles.bannerDescriptionShort}>
            By {productLine.doctor.name_display}
            <span className={styles.controlledSubstanceLine}>
              <ControlledSubstanceStateNotice
                summary={controlledSubsSummary}
                productLineNames={[productLine.name]}
              />
            </span>
          </span>
          <span className={styles.bannerDescriptionLong}>
            If you would like to request a different size/strength, place your order and{" "}
            {productLine.doctor.name_display} will review your request.{" "}
            <ControlledSubstanceStateNotice
              summary={controlledSubsSummary}
              productLineNames={[productLine.name]}
            />
          </span>
        </>
      }
    >
      <div className={cx(styles.prescription, prescriptions.length > 1 && styles.multi)}>
        {prescriptions.flatMap((prescription, i) => (
          <Fragment key={prescription.id}>
            {prescription.facts.map((fact) => (
              <div key={fact.label} className={fact.className} style={{ gridRowStart: i + 1 }}>
                <div className={styles.value}>{isValueSet(fact.value) ? fact.value : "-"}</div>
                <div className={styles.label}>{fact.label}</div>
              </div>
            ))}
          </Fragment>
        ))}
      </div>
    </ProductLinePrescriptionBanner>
  );
};

export default ProductLinePrescriptions;
